"use client";

import { useTranslations } from "next-intl";
import type { FontOption } from "./data";
import { assignRoles, contrastText } from "./helpers";

export function MobilePreview({
  colors,
  font,
}: {
  colors: string[];
  font: FontOption;
}) {
  const t = useTranslations("catalog.preview");
  const { bg, accent, secondary, surface } = assignRoles(colors);
  const bgText = contrastText(bg);
  const accentText = contrastText(accent);

  return (
    <div className="mx-auto w-[200px]">
      <div
        className="overflow-hidden rounded-[28px] border-[5px] shadow-xl transition-all duration-500"
        style={{ borderColor: "#1a1a24" }}
      >
        {/* notch */}
        <div
          className="flex items-center justify-between px-4 py-1"
          style={{ background: bg }}
        >
          <span
            className="text-[7px] font-semibold"
            style={{ color: `${bgText}66` }}
          >
            9:41
          </span>
          <div
            className="h-1 w-8 rounded-full"
            style={{ background: `${bgText}22` }}
          />
          <span className="text-[7px]" style={{ color: `${bgText}66` }}>
            ●●●
          </span>
        </div>
        <div
          style={{
            fontFamily: `"${font.name}", sans-serif`,
            background: bg,
            color: bgText,
          }}
        >
          <div
            style={{ borderBottom: `1px solid ${secondary}22` }}
            className="flex items-center justify-between px-3 py-2"
          >
            <span
              style={{ color: surface, fontWeight: 700 }}
              className="text-[10px]"
            >
              {t("brandName")}
            </span>
            <span
              style={{ color: `${bgText}55` }}
              className="text-base leading-none"
            >
              ≡
            </span>
          </div>
          <div className="px-3 py-3">
            <div
              className="mb-1.5 inline-block rounded-full px-2 py-0.5 text-[7px] font-semibold uppercase tracking-widest"
              style={{ background: `${accent}22`, color: accent }}
            >
              {t("eyebrow")}
            </div>
            <h1
              className="mb-1.5 text-sm font-bold leading-snug"
              style={{ color: surface }}
            >
              {t("headline")}
              <br />
              <span style={{ color: accent }}>{t("headlineAccent")}</span>
            </h1>
            <p
              className="mb-3 text-[8px] leading-relaxed"
              style={{ color: `${bgText}88` }}
            >
              {t("lede")}
            </p>
            <button
              style={{ background: accent, color: accentText }}
              className="w-full rounded-lg py-1.5 text-[9px] font-semibold"
            >
              {t("ctaPrimary")}
            </button>
          </div>
          <div className="space-y-1.5 px-3 pb-3">
            {(
              [
                ["⚡", t("card1Title")],
                ["✦", t("card2Title")],
                ["◎", t("card3Title")],
              ] as [string, string][]
            ).map(([icon, title]) => (
              <div
                key={title}
                style={{
                  background: `${surface}15`,
                  border: `1px solid ${secondary}22`,
                }}
                className="flex items-center gap-2 rounded-lg px-2.5 py-1.5"
              >
                <span className="text-xs">{icon}</span>
                <span
                  className="text-[9px] font-medium"
                  style={{ color: surface }}
                >
                  {title}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-center py-1.5" style={{ background: bg }}>
          <div
            className="h-0.5 w-10 rounded-full"
            style={{ background: `${bgText}22` }}
          />
        </div>
      </div>
    </div>
  );
}
